import { Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { useToast } from '../../library/useToast'
import { createOfApi, OF } from '../ofApi'
import { ErrorLine, Loading } from './kit'
import { findSection, NAV_ACCOUNT, NAV_GENERAL, NAV_TARGET, type NavId } from './sections'
import { asStr, SettingsContext, type OfMe, type SettingsCtx } from './settingsContext'
import '../../library/library.css'
import './settings.css'

interface Props {
  accountId: string
  /** The CRM's page.profile write: may change settings. */
  canWrite: boolean
  /** Account owner or super admin: Account security and login sessions. */
  isOwner: boolean
  /** This page's URL query (`s=<OnlyFans settings path>`). */
  query: string
  onOpenPage: (page: string, query?: string, opts?: { replace?: boolean }) => void
  mobileNav?: ReactNode
}

/**
 * Settings: onlyfans.com/my/settings (captured 2026-10-22). The left menu and
 * the open section side by side; on phones the menu, then the section on its
 * own. `?s=` is OnlyFans' own path under /my/settings.
 */
export function SettingsPage({ accountId, canWrite, isOwner, query, onOpenPage, mobileNav }: Props) {
  const { t } = useTranslation('ofSettings')
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const { say, node: toast } = useToast()
  const mobile = useIsNarrow()

  const requested = new URLSearchParams(query).get('s')
  const found = findSection(requested)
  // OnlyFans opens Profile when /my/settings has no section (phones show the menu).
  const current = found ?? (mobile ? null : findSection('profile'))

  const [me, setMe] = useState<OfMe | null>(null)
  const [failed, setFailed] = useState('')
  const loadMe = useCallback(async () => {
    const next = await of.get<OfMe>('users/me', {}, `${OF}/my/settings`)
    setMe(next ?? {})
  }, [of])
  useEffect(() => {
    setMe(null)
    setFailed('')
    loadMe().catch((e) => setFailed(e instanceof Error ? e.message : t('error')))
  }, [loadMe, t])

  const open = useCallback(
    (section: string) => {
      const id = NAV_TARGET[section as NavId] ?? section
      onOpenPage('settings', id ? `s=${encodeURIComponent(id).replace(/%2F/gi, '/')}` : '')
    },
    [onOpenPage],
  )

  const ctx: SettingsCtx | null = useMemo(
    () =>
      me && {
        accountId,
        of,
        me,
        reloadMe: loadMe,
        patchMe: async (body) => {
          const next = await of.send<OfMe>('PATCH', 'users/me', `${OF}/my/settings/profile`, body)
          const merged = { ...me, ...(next ?? {}) }
          setMe(merged)
          return merged
        },
        mergeMe: (fields) => setMe((prev) => ({ ...(prev ?? {}), ...fields })),
        canWrite,
        isOwner,
        open,
        openPage: (page, q) => onOpenPage(page, q),
        say,
      },
    [me, accountId, of, loadMe, canWrite, isOwner, open, onOpenPage, say],
  )

  const username = asStr(me?.username)
  const showMenu = !mobile || !current
  const showSection = Boolean(current) && (!mobile || Boolean(found))

  const menu = (
    <nav className="oset-nav" aria-label={t('title')}>
      <header className="oset-head">
        <button type="button" className="oset-head-btn" aria-label={t('back')} onClick={() => onOpenPage('more')}>
          <FeatherIcon name="arrow-left" size={24} />
        </button>
        <h1 className="oset-title">{t('title')}</h1>
      </header>
      <div className="oset-group">
        <div className="oset-row oset-group-title">
          <button type="button" className="oset-nav-user" onClick={() => onOpenPage('profile')}>
            {username ? `@${username}` : '…'}
          </button>
        </div>
        {NAV_ACCOUNT.map((id) => (
          <NavRow key={id} id={id} current={current?.nav === id} onOpen={open} />
        ))}
      </div>
      <div className="oset-group">
        <h3 className="oset-row oset-group-title">{t('nav.general')}</h3>
        {NAV_GENERAL.map((id) => (
          <NavRow key={id} id={id} current={current?.nav === id} onOpen={open} />
        ))}
      </div>
    </nav>
  )

  let section: ReactNode = null
  if (showSection && current) {
    if (failed) section = <ErrorLine onRetry={() => loadMe().catch((e) => setFailed(e instanceof Error ? e.message : t('error')))}>{failed}</ErrorLine>
    else if (!ctx) section = <Loading />
    else if (current.ownerOnly && !isOwner) section = <OwnerOnly />
    else {
      const Component = current.component
      section = (
        <Suspense fallback={<Loading />}>
          <Component key={current.id} section={current.id} />
        </Suspense>
      )
    }
  }

  return (
    <div className={`lib oset oset-page ${mobile ? 'lib--m oset-page--m' : ''}`}>
      <SettingsContext.Provider value={ctx}>
        <div className="oset-scroll">
          <div className="oset-columns">
            {showMenu && menu}
            {showSection && <main className="oset-content">{section}</main>}
          </div>
        </div>
      </SettingsContext.Provider>
      {toast}
      {mobile && mobileNav}
    </div>
  )
}

function NavRow({ id, current, onOpen }: { id: NavId; current: boolean; onOpen: (id: string) => void }) {
  const { t } = useTranslation('ofSettings')
  return (
    <button type="button" className={`oset-row oset-link ${current ? 'is-current' : ''}`} aria-current={current ? 'page' : undefined} onClick={() => onOpen(id)}>
      <span className="oset-row-text">
        <span className="oset-row-label">{t(`nav.${id}`)}</span>
      </span>
      <FeatherIcon name="chevron-right" size={24} className="oset-chevron" />
    </button>
  )
}

function OwnerOnly() {
  const { t } = useTranslation('ofSettings')
  return (
    <div className="oset-section">
      <div className="oset-empty">
        <FeatherIcon name="lock" size={28} />
        <p>{t('ownerOnly')}</p>
      </div>
    </div>
  )
}
